import type { Link } from "@bronte/viewer/src/data/ViewerConfig";
import { RendererTypes } from "@bronte/viewer/src/main";
import { DEVICE_TYPE, type DeviceType } from "../constants";

const EXTERNAL_LINK_PAYLOAD_TYPE = "ExternalLink" as const;

function isHttpExternalHref(attrHref: string | null, resolvedHref: string): boolean {
  if (!attrHref?.trim()) {
    return false;
  }
  const t = attrHref.trim();
  if (t.startsWith("#") || t.toLowerCase().startsWith("javascript:")) {
    return false;
  }
  try {
    const url = new URL(resolvedHref);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export type DelegatedExternalLinkOptions = {
  onLinkClick?: (data: Link) => void;
  deviceType?: DeviceType;
  renderer?: RendererTypes;
};

/**
 * One delegated `click` listener on the viewer content container: finds `a[target="_blank"]`
 * with an http(s) `href`, then `preventDefault` and `onLinkClick` only when
 * {@link RendererTypes.MOBILE} **and** `deviceType` is iOS or Android.
 */
export function attachDelegatedExternalLinkClick(
  container: HTMLElement,
  getOptions: () => DelegatedExternalLinkOptions,
): () => void {
  const listener = (event: MouseEvent) => {
    const { onLinkClick, deviceType, renderer } = getOptions();
    if (!onLinkClick) {
      return;
    }

    const rawTarget = event.target;
    if (!rawTarget || !(rawTarget instanceof Node) || !container.contains(rawTarget)) {
      return;
    }

    const element = rawTarget instanceof Element ? rawTarget : rawTarget.parentElement;
    if (!element) {
      return;
    }

    const anchor = element.closest("a[href]");
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }

    if (anchor.target !== "_blank") {
      return;
    }

    if (!isHttpExternalHref(anchor.getAttribute("href"), anchor.href)) {
      return;
    }

    const isNativeDevice = deviceType === DEVICE_TYPE.IOS || deviceType === DEVICE_TYPE.ANDROID;
    const isMobileRenderer = renderer === RendererTypes.MOBILE;
    if (isNativeDevice && isMobileRenderer) {
      event.preventDefault();

      onLinkClick({
        type: EXTERNAL_LINK_PAYLOAD_TYPE,
        href: anchor.href,
      });
    }
  };

  container.addEventListener("click", listener, false);
  return () => container.removeEventListener("click", listener, false);
}
